import { Outlet, Link, useParams, useRouterState } from '@tanstack/react-router';
import { useAreaConfig } from '@/hooks';
import { useAdminAreas } from '../hooks';
import { CurtinLogo } from '@/components/Header/CurtinLogo';
import { AccessDenied, NotFound, ServerError, Spinner } from '@/components/Common';

const LogoBlock = () => (
  <div className="flex gap-5 items-center flex-shrink-0">
    <CurtinLogo />
    <div className="font-bold text-xs flex flex-col text-[#F0F0F0] leading-tight">
      <span>CURTIN</span>
      <span className="font-normal">3D Admin</span>
    </div>
  </div>
);

const BackToViewer = ({ areaSlug }: { areaSlug?: string }) => (
  <Link
    to={areaSlug ? '/view/$areaSlug' : '/'}
    params={areaSlug ? { areaSlug } : undefined}
    className="text-sm font-bold text-[#F0F0F0] hover:text-lime transition-colors"
  >
    Back to viewer →
  </Link>
);

const NavLink = ({ to, params, children, capitalize: cap }: {
  to: string;
  params?: Record<string, string>;
  children: React.ReactNode;
  capitalize?: boolean;
}) => (
  <li className="relative flex items-center h-full">
    <Link
      to={to as any}
      params={params as any}
      className={`text-[#F0F0F0] hover:text-lime group${cap ? ' capitalize' : ''}`}
    >
      {children}
      <div className="absolute left-0 bottom-0 w-full h-1 bg-lime opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </Link>
  </li>
);

const NavActive = ({ children, capitalize: cap }: { children: React.ReactNode; capitalize?: boolean }) => (
  <li className="relative flex items-center h-full">
    <span className={`text-lime${cap ? ' capitalize' : ''}`}>{children}</span>
    <div className="absolute left-0 bottom-0 w-full h-1 bg-lime" />
  </li>
);

const MinimalHeader = ({ areaSlug }: { areaSlug?: string }) => (
  <header className="bg-black h-[56px] px-8 border-b border-[#b3b3b3] flex items-center justify-between flex-shrink-0">
    <LogoBlock />
    <BackToViewer areaSlug={areaSlug} />
  </header>
);

export const AdminLayout = () => {
  const { areaSlug, subjectSlug } = useParams({ strict: false }) as { areaSlug?: string; subjectSlug?: string };
  const { data: areaConfig, isLoading: areaLoading, error: areaError } = useAreaConfig(areaSlug, 'EDITOR');
  const { isLoading: areasLoading, error: areasError } = useAdminAreas({ enabled: !areaSlug });
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const singularName = areaConfig?.subjectName.singular ?? 'Subject';
  const pluralName   = areaConfig?.subjectName.plural   ?? 'Subjects';
  const isSubjectsList = pathname.endsWith('/subjects');
  const isAclPage = !!areaSlug && pathname.includes('/acl');
  const isAreaOverview = !!areaSlug && !pathname.includes('/subjects') && !pathname.includes('/acl');

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen flex flex-col font-sans admin-layout" style={{ backgroundColor: '#1A1A1A', color: '#E8E8E8' }}>
      {children}
    </div>
  );

  // Areas list page: gate on useAdminAreas before showing nav
  if (!areaSlug && areasLoading) {
    return shell(
      <>
        <MinimalHeader />
        <main className="flex-1 flex items-center justify-center">
          <Spinner />
        </main>
      </>
    );
  }

  if (!areaSlug && areasError) {
    let errorContent: React.ReactNode;
    if (areasError.data?.code === 'FORBIDDEN') {
      errorContent = <AccessDenied error={areasError.data.accessError as any} />;
    } else {
      errorContent = <ServerError />;
    }
    return shell(
      <>
        <MinimalHeader />
        <main className="flex-1">{errorContent}</main>
      </>
    );
  }

  // Area-specific pages: gate on useAreaConfig before showing nav
  if (areaSlug && areaLoading) {
    return shell(
      <>
        <MinimalHeader areaSlug={areaSlug} />
        <main className="flex-1 flex items-center justify-center">
          <Spinner />
        </main>
      </>
    );
  }

  if (areaSlug && areaError) {
    let errorContent: React.ReactNode;
    switch (areaError.data?.code) {
      case 'FORBIDDEN':
        errorContent = <AccessDenied error={areaError.data.accessError as any} />;
        break;
      case 'NOT_FOUND':
        errorContent = <NotFound message={`Area "${areaSlug}" could not be found.`} />;
        break;
      default:
        errorContent = <ServerError />;
    }
    return shell(
      <>
        <MinimalHeader areaSlug={areaSlug} />
        <main className="flex-1">{errorContent}</main>
      </>
    );
  }

  // Access confirmed: full nav
  return shell(
    <>
      <header className="bg-black h-[56px] px-8 border-b border-[#b3b3b3] flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-16">
          <LogoBlock />
          <ul className="flex gap-5 text-sm font-bold h-[56px] items-center">

            {/* Areas — active on /admin/areas, link on area-specific pages */}
            {!areaSlug
              ? <NavActive>Areas</NavActive>
              : <NavLink to="/admin/areas">Areas</NavLink>
            }

            {/* Area title — only when EDITOR+ access confirmed (areaConfig loaded) */}
            {areaSlug && areaConfig && (
              isAreaOverview
                ? <NavActive>{areaConfig.title}</NavActive>
                : <NavLink to="/admin/areas/$areaSlug" params={{ areaSlug }}>{areaConfig.title}</NavLink>
            )}

            {/* ACL page */}
            {areaSlug && areaConfig && isAclPage && (
              <NavActive>Access Control</NavActive>
            )}

            {/* Subjects link — when inside subject context, not on subjects list or ACL page */}
            {areaSlug && areaConfig && !isAreaOverview && !isAclPage && !isSubjectsList && (
              <NavLink to="/admin/areas/$areaSlug/subjects" params={{ areaSlug }} capitalize>
                {pluralName}
              </NavLink>
            )}

            {/* Active subject context — subjects list, add, or edit */}
            {areaSlug && areaConfig && !isAreaOverview && !isAclPage && (
              <NavActive capitalize>
                {isSubjectsList
                  ? pluralName
                  : subjectSlug
                    ? `Edit ${singularName}`
                    : `Add ${singularName}`}
              </NavActive>
            )}

          </ul>
        </div>
        <BackToViewer areaSlug={areaSlug} />
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </>
  );
};
